import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { Badge, Box, Button, Group, Loader, Select, Stack, Text } from '@mantine/core';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import classes from './EntityPage.module.css';

const STEP_LABELS: Record<string, string> = {
  domain: '1 · Домен',
  entity: '2 · Сутність',
  geo: '3 · ГЕО',
  pages: '4 · Сторінки',
  template: '5 · Шаблон',
  assemble: '6 · Збірка',
};

const STEPS = ['domain', 'entity', 'geo', 'pages', 'template', 'assemble'] as const;

/** Resolved values are `unknown`; only primitives and lists have a sensible rendering. */
function formatResolved(value: unknown): string {
  if (Array.isArray(value)) return value.map((item) => formatResolved(item)).join(', ');
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/**
 * Візард збірки (§2), acceptance scenario 7.
 *
 * The six steps are a state, not a set of screens: the operator sees where the build stopped
 * and **why**. Blockers are split into fatal and non-fatal because they need different
 * actions — one is "fix this before anything happens", the other is "this is what the site
 * will be missing".
 */
export function BuildPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  /** Сайт може прийти з власної картки — вкладка «Сторінки», кнопка «Зібрати». */
  const search = useSearch({ from: '/authed/build' });
  const [siteId, setSiteId] = useState<string | null>(search.siteId ?? null);


  const sites = useQuery(
    trpc.sites.list.queryOptions({ pagination: { limit: 200, direction: 'desc' } }),
  );

  const latest = useQuery({
    ...trpc.builds.latestForSite.queryOptions({ siteId: siteId ?? '' }),
    enabled: siteId !== null,
  });

  /** Той самий перелік змін, що й у картці сайту — «що саме поїде», а не «чи можна». */
  const queue = useQuery({
    ...trpc.sites.deployQueue.queryOptions({ id: siteId ?? '' }),
    enabled: siteId !== null,
  });

  const buildId = latest.data?.id ?? null;

  const summary = useQuery({
    ...trpc.builds.summary.queryOptions({ id: buildId ?? '' }),
    enabled: buildId !== null,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.builds.pathKey() });
  };

  const start = useMutation(trpc.builds.start.mutationOptions({ onSuccess: invalidate }));
  const run = useMutation(trpc.builds.run.mutationOptions({ onSuccess: invalidate }));
  const assemble = useMutation(trpc.builds.assemble.mutationOptions({ onSuccess: invalidate }));

  const state = summary.data?.state;
  const blockers = state?.blockers ?? [];
  const fatal = blockers.filter((b) => b.fatal);
  const warnings = blockers.filter((b) => !b.fatal);

  /**
   * Чому «Зібрати» не тиснеться — словами, а не сірою кнопкою.
   *
   * Чотири різні причини давали однаковий мертвий елемент: збірки ще немає, є фатальний
   * блокер, кроки ще йдуть, кроки не дійшли до збірки. Оператор бачить те саме, що бачив
   * прогін, який три хвилини чекав на кнопку, — і жодного натяку, що робити.
   *
   * Взірець поруч: чекліст деплою відмовляє з названою причиною, і саме тому його не
   * плутають зі зламаним екраном.
   */
  const assembleBlocker = ((): string | null => {
    if (buildId === null) return 'Спершу почніть збірку';
    if (run.isPending) return 'Кроки ще йдуть';
    if (fatal.length > 0) return 'Є фатальні блокери — закрийте їх і пройдіть кроки ще раз';
    if (state?.step !== 'assemble') return 'Кроки ще не дійшли до збірки — натисніть «Пройти кроки»';
    return null;
  })();

  return (
    <Stack gap="md">
      <Group gap="md" align="flex-end">
        <Select
          label="Сайт"
          placeholder="домен"
          data={(sites.data?.items ?? []).map((s) => ({ value: s.id, label: s.domain }))}
          value={siteId}
          onChange={setSiteId}
          searchable
          data-testid="build-site"
          style={{ width: 260 }}
        />
        <Button
          size="xs"
          disabled={siteId === null}
          loading={start.isPending}
          data-testid="build-start"
          onClick={() => {
            if (siteId !== null) start.mutate({ siteId });
          }}
        >
          Почати збірку
        </Button>
        <Button
          size="xs"
          variant="default"
          disabled={buildId === null}
          loading={run.isPending}
          data-testid="build-run"
          onClick={() => {
            if (buildId !== null) run.mutate({ id: buildId });
          }}
        >
          Пройти кроки
        </Button>
        <Button
          size="xs"
          // Also blocked while the steps are still running: assembling before the template
          // step has taken its snapshot refuses, and the refusal reads like a broken build.
          disabled={assembleBlocker !== null}
          loading={assemble.isPending}
          data-testid="build-assemble"
          onClick={() => {
            if (buildId !== null) assemble.mutate({ id: buildId });
          }}
        >
          Зібрати
        </Button>
        {assembleBlocker === null ? null : (
          <Text className={classes.dim} data-testid="build-assemble-blocked">
            {assembleBlocker}
          </Text>
        )}
      </Group>

      {start.error === null ? null : (
        <Text className={classes.danger} data-testid="build-error">
          {publicMessageOf(start.error)}
        </Text>
      )}
      {assemble.error === null ? null : (
        <Text className={classes.danger} data-testid="build-assemble-error">
          {publicMessageOf(assemble.error)}
        </Text>
      )}

      {/*
        * «Що зміниться» — крок мокапа перед запуском збірки.
        *
        * Той самий перелік, що й у картці сайту: чеклист відповідає «чи можна», а це — «що
        * саме». Без нього збірка запускається наосліп, а зміст дізнаються з диффа постфактум.
        */}
      {siteId === null ? null : (
        <Box className={classes.panel} data-testid="build-queue-panel">
          <Text className={classes.panelTitle} mb={8}>
            Що зміниться
          </Text>
          {queue.error ? (
            <Text className={classes.danger} data-testid="build-queue-error">
              {publicMessageOf(queue.error)}
            </Text>
          ) : queue.data === undefined ? (
            <Loader size="sm" />
          ) : queue.data.changes.length === 0 ? (
            <Text className={classes.dim} data-testid="build-queue-empty">
              {queue.data.deployedAt === null
                ? 'Сайт ще не виливався — збірка буде першою.'
                : 'Від останньої виливки нічого не змінилось.'}
            </Text>
          ) : (
            <Stack gap={4}>
              {queue.data.changes.map((change, index) => (
                <Text
                  key={`${change.kind}-${String(index)}`}
                  className={classes.dim}
                  data-testid={`build-change-${String(index)}`}
                >
                  {change.label}
                </Text>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {siteId === null ? (
        <Box className={classes.panel} data-testid="build-no-site">
          <Text className={classes.dim}>Оберіть сайт, щоб побачити його збірку.</Text>
        </Box>
      ) : latest.isPending ? (
        <Loader size="sm" />
      ) : state === undefined ? (
        <Box className={classes.panel} data-testid="build-none">
          <Text className={classes.dim}>Цей сайт ще не збирався.</Text>
        </Box>
      ) : (
        <>
          <Box className={classes.panel} data-testid="build-steps">
            <Group gap="xs" mb="sm">
              {STEPS.map((step) => {
                const index = STEPS.indexOf(step);
                const currentIndex = STEPS.indexOf(state.step);
                const done = index < currentIndex;
                const current = index === currentIndex;
                return (
                  <Badge
                    key={step}
                    size="sm"
                    color={
                      current && state.status === 'error' ? 'rose' : done ? 'mint' : current ? 'azure' : 'graphite'
                    }
                    variant={done || current ? 'filled' : 'default'}
                    data-step-state={done ? 'done' : current ? 'current' : 'pending'}
                    data-testid={`step-${step}`}
                  >
                    {STEP_LABELS[step] ?? step}
                  </Badge>
                );
              })}
            </Group>

            <Group gap="lg">
              <Text className={classes.dim}>
                домен <span className={classes.mono}>{summary.data?.siteDomain ?? '—'}</span>
              </Text>
              <Text className={classes.dim}>
                ГЕО <span className={classes.mono}>{summary.data?.geoCode ?? '—'}</span>
              </Text>
              <Text className={classes.dim}>
                статус{' '}
                <span className={classes.mono} data-testid="build-status">
                  {state.status}
                </span>
              </Text>
            </Group>
          </Box>

          <Box className={classes.panel} data-testid="build-blockers">
            <Text className={classes.panelTitle} mb={8}>
              Блокери
            </Text>

            {fatal.length === 0 ? (
              <Text className={classes.mono} data-testid="build-no-fatal">
                ✓ нічого не заважає збірці
              </Text>
            ) : (
              <Stack gap={4} mb="sm">
                {fatal.map((blocker, index) => (
                  <Group key={`${blocker.step}-${String(index)}`} gap={8} data-testid={`fatal-${blocker.step}`}>
                    <Badge color="rose" size="sm">
                      {STEP_LABELS[blocker.step] ?? blocker.step}
                    </Badge>
                    <Text className={classes.danger}>{blocker.message}</Text>
                  </Group>
                ))}
              </Stack>
            )}

            {warnings.length === 0 ? null : (
              <Stack gap={4}>
                <Text className={classes.dim}>Не блокує, але сайт це втратить:</Text>
                {warnings.map((blocker, index) => (
                  <Group
                    key={`${blocker.step}-w-${String(index)}`}
                    gap={8}
                    data-testid={`warning-${blocker.step}`}
                  >
                    <Badge color="amber" size="sm">
                      {STEP_LABELS[blocker.step] ?? blocker.step}
                    </Badge>
                    <Text className={classes.dim}>{blocker.message}</Text>
                  </Group>
                ))}
              </Stack>
            )}
          </Box>

          <Box className={classes.panel} data-testid="build-resolved">
            <Text className={classes.panelTitle} mb={8}>
              Що резолвнулось
            </Text>
            <Group gap="lg" wrap="wrap">
              {Object.entries(state.resolved).map(([key, value]) => (
                <Text className={classes.dim} key={key} data-testid={`resolved-${key}`}>
                  {key}{' '}
                  <span className={classes.mono}>{formatResolved(value)}</span>
                </Text>
              ))}
            </Group>
          </Box>

          {state.pagesSummary.length === 0 ? null : (
            <Box className={classes.panel} data-testid="build-pages">
              <Text className={classes.panelTitle} mb={8}>
                Сторінки до збірки
              </Text>
              <Group gap={6}>
                {state.pagesSummary.map((page) => (
                  <Badge key={page.url} size="sm" data-testid={`build-page-${page.url}`}>
                    {page.pageType} · {page.url}
                  </Badge>
                ))}
              </Group>
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}
